import {
    Alert,
    Button,
    Checkbox,
    Collapse,
    Input,
    Modal,
    Radio,
    Select,
    Tag,
    Typography
} from "antd";
import {
    CalendarClock,
    Check,
    Clock3,
    Cloud,
    Copy,
    Database,
    Boxes,
    Server,
    Workflow
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
    ManagedResourceType,
    Scheduler,
    SchedulerMode,
    UpsertSchedulerRequest
} from "../../../backend/cost-optimizer";
import { AWS_REGIONS } from "../../../enums/enums";

const { Text } = Typography;

export interface SchedulerEnvironment {
    id: string;
    name: string;
    awsAccountId?: string;
    awsRegion?: string;
    region?: string;
}

export interface SchedulerAwsAccount {
    id: string;
    name: string;
}

interface Props {
    environments: SchedulerEnvironment[];
    awsAccounts: SchedulerAwsAccount[];
    existingSchedulerIds: string[];
    scheduler?: Scheduler;
    open: boolean;
    saving: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (environmentId: string, request: UpsertSchedulerRequest) => Promise<void>;
}

type SchedulerSource = "environment" | "account";

const DAYS = [
    { value: "MON", label: "Mon" },
    { value: "TUE", label: "Tue" },
    { value: "WED", label: "Wed" },
    { value: "THU", label: "Thu" },
    { value: "FRI", label: "Fri" },
    { value: "SAT", label: "Sat" },
    { value: "SUN", label: "Sun" }
];

const COMMON_TIMEZONES = [
    "UTC",
    "Australia/Sydney",
    "Australia/Melbourne",
    "Europe/Skopje",
    "Europe/London",
    "Europe/Berlin",
    "America/New_York",
    "America/Chicago",
    "America/Los_Angeles",
    "Asia/Singapore",
    "Asia/Tokyo"
];

const RESOURCE_OPTIONS: Array<{
    type: ManagedResourceType;
    title: string;
    description: string;
    icon: typeof Server;
    defaultEnabled: boolean;
}> = [
    {
        type: "ec2-instance",
        title: "EC2 instances",
        description: "Stop instances and restore only those stopped by this scheduler.",
        icon: Server,
        defaultEnabled: true
    },
    {
        type: "ecs-service",
        title: "ECS and Fargate",
        description: "Scale services to zero, then restore their previous desired count.",
        icon: Workflow,
        defaultEnabled: true
    },
    {
        type: "eks-node-group",
        title: "EKS managed node groups",
        description: "Scale through EKS and restore the previous minimum, maximum, and desired sizes.",
        icon: Boxes,
        defaultEnabled: true
    },
    {
        type: "rds-instance",
        title: "RDS instances",
        description: "Stop provisioned database instances and start them on demand.",
        icon: Database,
        defaultEnabled: true
    },
    {
        type: "rds-cluster",
        title: "RDS clusters",
        description: "Manage provisioned Aurora clusters. Serverless clusters stay untouched.",
        icon: Database,
        defaultEnabled: true
    },
    {
        type: "auto-scaling-group",
        title: "Auto Scaling groups",
        description: "Scale standalone groups to zero and restore their previous capacity.",
        icon: Server,
        defaultEnabled: false
    }
];

function browserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function allTimezones(selected: string) {
    try {
        const values = (Intl as typeof Intl & { supportedValuesOf: (key: "timeZone") => string[] }).supportedValuesOf("timeZone");
        return Array.from(new Set([selected, "UTC", ...values]));
    } catch {
        return Array.from(new Set([selected, ...COMMON_TIMEZONES]));
    }
}

function schedulerRegion(environment?: SchedulerEnvironment) {
    return environment?.awsRegion || environment?.region;
}

function slug(value: string) {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

export default function ScheduleEditorDialog({
    environments,
    awsAccounts,
    existingSchedulerIds,
    scheduler,
    open,
    saving,
    onOpenChange,
    onSave
}: Props) {
    const usableEnvironments = useMemo(
        () => environments.filter(environment => environment.awsAccountId && schedulerRegion(environment)),
        [environments]
    );
    const [source, setSource] = useState<SchedulerSource>("environment");
    const [environmentId, setEnvironmentId] = useState("");
    const [awsAccountId, setAwsAccountId] = useState("");
    const [awsRegion, setAwsRegion] = useState("");
    const [name, setName] = useState("");
    const [resourceGroup, setResourceGroup] = useState("");
    const [mode, setMode] = useState<SchedulerMode>("automatic");
    const [timezone, setTimezone] = useState(browserTimezone());
    const [days, setDays] = useState<string[]>(DAYS.slice(0, 5).map(day => day.value));
    const [startTime, setStartTime] = useState("08:00");
    const [stopTime, setStopTime] = useState("18:00");
    const [safetyStopTime, setSafetyStopTime] = useState("20:00");
    const [defaultLeaseMinutes, setDefaultLeaseMinutes] = useState(480);
    const [managedTypes, setManagedTypes] = useState<ManagedResourceType[]>(
        RESOURCE_OPTIONS.filter(option => option.defaultEnabled).map(option => option.type)
    );
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open) return;

        const matchingEnvironment = scheduler
            ? environments.find(environment => environment.id === scheduler.environmentId)
            : usableEnvironments[0];
        const initialSource: SchedulerSource = matchingEnvironment || (!scheduler && usableEnvironments.length)
            ? "environment"
            : "account";
        const initialAccount = scheduler?.awsAccountId || matchingEnvironment?.awsAccountId || awsAccounts[0]?.id || "";
        const initialRegion = scheduler?.awsRegion || schedulerRegion(matchingEnvironment) || "";
        const initialName = scheduler?.name || (matchingEnvironment ? `${matchingEnvironment.name} schedule` : "");

        setSource(initialSource);
        setEnvironmentId(matchingEnvironment?.id || "");
        setAwsAccountId(initialAccount);
        setAwsRegion(initialRegion);
        setName(initialName);
        setResourceGroup(scheduler && !matchingEnvironment ? scheduler.environmentId : "");
        setMode(scheduler?.mode || "automatic");
        setTimezone(scheduler?.timezone || browserTimezone());
        setDays(scheduler?.automaticSchedule.daysOfWeek || DAYS.slice(0, 5).map(day => day.value));
        setStartTime(scheduler?.automaticSchedule.startTime || "08:00");
        setStopTime(scheduler?.automaticSchedule.stopTime || "18:00");
        setSafetyStopTime(scheduler?.lease.safetyStopTime || "20:00");
        setDefaultLeaseMinutes(scheduler?.lease.defaultMinutes || 480);
        setManagedTypes(
            scheduler?.managedResourceTypes || RESOURCE_OPTIONS.filter(option => option.defaultEnabled).map(option => option.type)
        );
        setError("");
        setCopied(false);
    }, [open, scheduler, environments, usableEnvironments, awsAccounts]);

    const selectedEnvironment = usableEnvironments.find(environment => environment.id === environmentId);
    const timezones = useMemo(() => allTimezones(timezone), [timezone]);
    const effectiveGroup = scheduler && source === "account" ? scheduler.environmentId : resourceGroup.trim();
    const tagValue = effectiveGroup || "your-resource-group";
    const resourceSummary = RESOURCE_OPTIONS
        .filter(option => managedTypes.includes(option.type))
        .map(option => option.title.replace(" instances", "").replace(" clusters", ""))
        .join(", ");

    const selectEnvironment = (value: string) => {
        const environment = usableEnvironments.find(item => item.id === value);
        setEnvironmentId(value);
        setAwsAccountId(environment?.awsAccountId || "");
        setAwsRegion(schedulerRegion(environment) || "");
        if (!scheduler && environment) setName(`${environment.name} schedule`);
    };

    const selectAccount = (value: string) => {
        const account = awsAccounts.find(item => item.id === value);
        const relatedEnvironment = usableEnvironments.find(environment => environment.awsAccountId === value);
        setAwsAccountId(value);
        setAwsRegion(schedulerRegion(relatedEnvironment) || "");
        if (!scheduler && account) {
            const generatedName = `${account.name} schedule`;
            setName(generatedName);
            setResourceGroup(slug(generatedName));
        }
    };

    const updateName = (value: string) => {
        setName(value);
        if (!scheduler && source === "account" && (!resourceGroup || resourceGroup === slug(name))) {
            setResourceGroup(slug(value));
        }
    };

    const copyTag = async () => {
        await navigator.clipboard?.writeText(`microtica:environment=${tagValue}`);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
    };

    const submit = async () => {
        const targetId = source === "environment" ? environmentId : effectiveGroup;
        const targetAccountId = source === "environment" ? selectedEnvironment?.awsAccountId : awsAccountId;
        const targetRegion = source === "environment" ? schedulerRegion(selectedEnvironment) : awsRegion;

        if (!targetId || !targetAccountId || !targetRegion) {
            setError(source === "environment"
                ? "Choose an AWS environment with an account and region."
                : "Choose an AWS account, region, and resource group.");
            return;
        }
        if (!name.trim()) {
            setError("Enter a scheduler name.");
            return;
        }
        if (source === "account" && !/^[A-Za-z0-9._:-]{1,128}$/.test(targetId)) {
            setError("Use letters, numbers, dots, colons, underscores, or hyphens for the resource group.");
            return;
        }
        if (!scheduler && existingSchedulerIds.includes(targetId)) {
            setError("A scheduler already uses this environment or resource group.");
            return;
        }
        if (mode === "automatic" && days.length === 0) {
            setError("Choose at least one running day.");
            return;
        }
        if (mode === "automatic" && startTime >= stopTime) {
            setError("Stop time must be later than start time.");
            return;
        }
        if (managedTypes.length === 0) {
            setError("Choose at least one resource type.");
            return;
        }

        setError("");
        try {
            await onSave(targetId, {
                name: name.trim(),
                awsAccountId: targetAccountId,
                awsRegion: targetRegion,
                mode,
                timezone,
                automaticSchedule: { daysOfWeek: days, startTime, stopTime },
                lease: {
                    defaultMinutes: defaultLeaseMinutes,
                    maxMinutes: 24 * 60,
                    safetyStopTime
                },
                managedResourceTypes: managedTypes,
                excludedResourceIds: scheduler?.excludedResourceIds || []
            });
            onOpenChange(false);
        } catch (saveError) {
            const candidate = saveError as { response?: { data?: { message?: string } }; message?: string };
            setError(candidate.response?.data?.message || candidate.message || "Could not save the scheduler.");
        }
    };

    return (
        <Modal
            className="optimizer-schedule-modal"
            title={scheduler ? "Edit scheduler" : "Create scheduler"}
            open={open}
            width={720}
            centered
            destroyOnClose
            onCancel={() => onOpenChange(false)}
            footer={[
                <Button key="cancel" onClick={() => onOpenChange(false)}>Cancel</Button>,
                <Button key="save" type="primary" loading={saving} onClick={submit}>
                    {scheduler ? "Save changes" : "Create scheduler"}
                </Button>
            ]}
        >
            <div className="optimizer-form-section">
                <div className="optimizer-form-section__heading">
                    <span>1</span>
                    <div>
                        <strong>Choose what to manage</strong>
                        <Text type="secondary">Use a Microtica environment or any connected AWS account.</Text>
                    </div>
                </div>

                {scheduler ? (
                    <div className="optimizer-target-summary">
                        <span className="optimizer-target-summary__icon"><Cloud size={18} /></span>
                        <div>
                            <strong>{selectedEnvironment?.name || scheduler.name}</strong>
                            <Text type="secondary">{scheduler.awsAccountId} · {scheduler.awsRegion}</Text>
                        </div>
                        <Tag>{selectedEnvironment ? "Microtica environment" : "AWS account"}</Tag>
                    </div>
                ) : (
                    <>
                        <Radio.Group
                            className="optimizer-source-picker"
                            value={source}
                            onChange={event => {
                                const nextSource = event.target.value as SchedulerSource;
                                setSource(nextSource);
                                setError("");
                                if (nextSource === "environment" && usableEnvironments[0]) selectEnvironment(usableEnvironments[0].id);
                                if (nextSource === "account" && awsAccounts[0]) selectAccount(awsAccounts[0].id);
                            }}
                        >
                            <Radio.Button value="environment" disabled={!usableEnvironments.length}>
                                <Cloud size={17} />
                                <span><strong>Microtica environment</strong><small>Account, region, and resource tag are ready</small></span>
                            </Radio.Button>
                            <Radio.Button value="account" disabled={!awsAccounts.length}>
                                <Server size={17} />
                                <span><strong>AWS account</strong><small>Manage tagged resources without an environment</small></span>
                            </Radio.Button>
                        </Radio.Group>

                        {source === "environment" ? (
                            <div className="optimizer-field">
                                <label>Environment</label>
                                <Select
                                    value={environmentId || undefined}
                                    placeholder="Choose an environment"
                                    onChange={selectEnvironment}
                                    options={usableEnvironments.map(environment => ({ value: environment.id, label: environment.name }))}
                                />
                                {selectedEnvironment && <Text type="secondary">{selectedEnvironment.awsAccountId} · {schedulerRegion(selectedEnvironment)}</Text>}
                            </div>
                        ) : (
                            <div className="optimizer-account-grid">
                                <div className="optimizer-field">
                                    <label>AWS account</label>
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        value={awsAccountId || undefined}
                                        placeholder="Choose an account"
                                        onChange={selectAccount}
                                        options={awsAccounts.map(account => ({ value: account.id, label: `${account.name} · ${account.id}` }))}
                                    />
                                </div>
                                <div className="optimizer-field">
                                    <label>Region</label>
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        value={awsRegion || undefined}
                                        placeholder="Choose a region"
                                        onChange={setAwsRegion}
                                        options={AWS_REGIONS.map(region => ({ value: region.value, label: `${region.name} · ${region.value}` }))}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="optimizer-field">
                    <label>Scheduler name</label>
                    <Input
                        value={name}
                        maxLength={100}
                        placeholder={source === "environment" ? "Development schedule" : "Shared dev account"}
                        onChange={event => updateName(event.target.value)}
                    />
                </div>

                {source === "account" && (
                    <div className="optimizer-standalone-scope">
                        <div className="optimizer-field">
                            <label>Resource group</label>
                            <Input
                                value={effectiveGroup}
                                disabled={Boolean(scheduler)}
                                placeholder="development"
                                prefix={<Text type="secondary">microtica:environment =</Text>}
                                onChange={event => setResourceGroup(slug(event.target.value))}
                                suffix={effectiveGroup ? <Button type="text" size="small" icon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copyTag} /> : undefined}
                            />
                        </div>
                        <Alert
                            type="info"
                            showIcon
                            message="Only tagged resources are managed"
                            description={<span>Add <code>microtica:environment={tagValue}</code> to the EC2, ECS, EKS node groups, RDS, and Auto Scaling resources you want this scheduler to control.</span>}
                        />
                    </div>
                )}
            </div>

            <div className="optimizer-form-section">
                <div className="optimizer-form-section__heading">
                    <span>2</span>
                    <div>
                        <strong>Choose when resources run</strong>
                        <Text type="secondary">Start with a common pattern. You can change it later.</Text>
                    </div>
                </div>

                <Radio.Group className="optimizer-mode-picker" value={mode} onChange={event => setMode(event.target.value)}>
                    <Radio.Button value="automatic">
                        <CalendarClock size={18} />
                        <span><strong>Working hours</strong><small>Start and stop on a weekly schedule</small></span>
                    </Radio.Button>
                    <Radio.Button value="manual">
                        <Clock3 size={18} />
                        <span><strong>On-demand leases</strong><small>Start for a fixed time, then stop automatically</small></span>
                    </Radio.Button>
                </Radio.Group>

                {mode === "automatic" ? (
                    <div className="optimizer-schedule-fields">
                        <div className="optimizer-field optimizer-field--full">
                            <label>Running days</label>
                            <Checkbox.Group className="optimizer-day-picker" value={days} onChange={values => setDays(values as string[])} options={DAYS} />
                        </div>
                        <div className="optimizer-field">
                            <label>Start at</label>
                            <Input type="time" value={startTime} onChange={event => setStartTime(event.target.value)} />
                        </div>
                        <div className="optimizer-field">
                            <label>Stop at</label>
                            <Input type="time" value={stopTime} onChange={event => setStopTime(event.target.value)} />
                        </div>
                    </div>
                ) : (
                    <div className="optimizer-schedule-fields">
                        <div className="optimizer-field">
                            <label>Default lease</label>
                            <Select
                                value={defaultLeaseMinutes}
                                onChange={setDefaultLeaseMinutes}
                                options={[
                                    { value: 60, label: "1 hour" },
                                    { value: 120, label: "2 hours" },
                                    { value: 240, label: "4 hours" },
                                    { value: 480, label: "8 hours" },
                                    { value: 720, label: "12 hours" },
                                    { value: 1440, label: "24 hours" }
                                ]}
                            />
                        </div>
                        <div className="optimizer-field">
                            <label>Daily safety stop</label>
                            <Input type="time" value={safetyStopTime} onChange={event => setSafetyStopTime(event.target.value)} />
                        </div>
                    </div>
                )}

                <div className="optimizer-field optimizer-timezone-field">
                    <label>Timezone</label>
                    <Select showSearch optionFilterProp="label" value={timezone} onChange={setTimezone} options={timezones.map(value => ({ value, label: value }))} />
                    <Text type="secondary">Schedule times follow this timezone, including daylight-saving changes.</Text>
                </div>
            </div>

            <div className="optimizer-form-section optimizer-form-section--last">
                <div className="optimizer-form-section__heading">
                    <span>3</span>
                    <div>
                        <strong>Review managed services</strong>
                        <Text type="secondary">The safe defaults cover the common stoppable AWS resources.</Text>
                    </div>
                </div>
                <Collapse
                    className="optimizer-resource-collapse"
                    items={[{
                        key: "resources",
                        label: (
                            <div className="optimizer-resource-collapse__label">
                                <span>{managedTypes.length} resource types selected</span>
                                <Text type="secondary">{resourceSummary}</Text>
                            </div>
                        ),
                        children: (
                            <div className="optimizer-resource-options">
                                {RESOURCE_OPTIONS.map(option => {
                                    const Icon = option.icon;
                                    const checked = managedTypes.includes(option.type);
                                    return (
                                        <label className={`optimizer-resource-option${checked ? " optimizer-resource-option--selected" : ""}`} key={option.type}>
                                            <Checkbox
                                                checked={checked}
                                                onChange={event => setManagedTypes(current => event.target.checked ? [...current, option.type] : current.filter(value => value !== option.type))}
                                            />
                                            <span className="optimizer-resource-option__icon"><Icon size={17} /></span>
                                            <span><strong>{option.title}</strong><small>{option.description}</small></span>
                                        </label>
                                    );
                                })}
                            </div>
                        )
                    }]}
                />
            </div>

            {error && <Alert className="optimizer-form-error" type="error" showIcon message={error} />}
            {!usableEnvironments.length && !awsAccounts.length && !scheduler && (
                <Alert className="optimizer-form-error" type="warning" showIcon message="Connect an AWS account before creating a scheduler." />
            )}
        </Modal>
    );
}
